import React, { useState } from 'react'
import { Dotsmenu } from '@e3dc-react/icons'
import { Box, IconButton, Menu, MenuItem } from '@mui/material'
import { WallboxModel } from '../models/wallbox-model'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import useModals from 'src/Hooks/useModals'
import { useHagerBridgePermissions } from '../../shared/hooks/use-hager-bridge-permissions'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { updateChargingSessionsFilters } from 'src/State/E-Mobility/EMobilityStateAction'

interface WallboxMenuProps {
  wallbox: WallboxModel
  installationId: number
}

/**
 *  3 dots menu component used in every wallbox card.
 *  @param wallbox - single WallboxModel
 *  @param installationId - unique installation identifier as string
 *  @param user - user data from account link which match logged user
 */
export const WallboxMenu: React.FC<WallboxMenuProps> = ({ wallbox, installationId }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isOpen = Boolean(anchorEl)
  const { t } = useCustomTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = (): void => {
    setAnchorEl(null)
  }

  const {
    permissions: { wallboxPermissions },
  } = useHagerBridgePermissions()

  const { openAssignTariffDialog } = useModals()

  /**
   * The condition wallbox.activeTariff?.tariffCategory === 'DYNAMIC' can be replaced with checking the global flag returned from
   * GET /tariffs endpoint(dynamicTariffActivated).
   *
   * If dynamic tariff is activated, this means all wallboxes of the installation will have the dynamic tariff automatically assigned to it, so
   * we don't need to check each individual wallbox. However, this approach will work correctly in cases where database entries are edited
   * manually for testing purposes.
   */
  const isTariffDynamic = wallbox.activeTariff?.tariffCategory === 'DYNAMIC'
  const isAssignTariffDisabled = !wallboxPermissions.assignTariff || isTariffDynamic

  return (
    <Box id="e-mobility-wallbox-options-menu-button">
      <IconButton onClick={handleClick}>
        <Dotsmenu />
      </IconButton>
      <Menu anchorEl={anchorEl} open={isOpen} onClose={handleClose}>
        <MenuItem
          disabled={isAssignTariffDisabled}
          onClick={() => {
            openAssignTariffDialog({ wallbox, installationId })
            handleClose()
          }}
        >
          <Box sx={{ maxWidth: 220, min: 0, overflowWrap: 'break-word', wordBreak: 'break-word', whiteSpace: 'normal' }}>
            {isTariffDynamic ? t('emobility.wallbox.edit-dynamic-tariffs') : t('emobility.wallbox.edit-tariffs')}
          </Box>
        </MenuItem>
        <MenuItem
          onClick={() => {
            dispatch(updateChargingSessionsFilters({ wallboxIds: [wallbox.wallboxId], page: 0 }))
            navigate(`/e-mobility/${installationId}/charging-sessions`)
          }}
        >
          {t('emobility.wallbox.view-sessions')}
        </MenuItem>
      </Menu>
    </Box>
  )
}
